<template>
  <div class="results-bar">
    <div class="results-bar-left row">
      <div v-if="!loading" class="results-bar-count row">
        <p class="text-secondary-600">
          {{ productsQty ?? 0 }}
        </p>
        <p class="lowercase text-neutral-900">
          {{ t("PRODUCTS_QTY", { n: productsQty }) }}
        </p>
      </div>
      <category-buttons-filter-button
        v-if="hasFiltersButton"
        :count="filtersQty"
        class="hidden md:flex"
        @click="$emit('openFilter')"
      />
      <category-buttons-catalog-view-switch class="flex gap-2 md:hidden" />
    </div>
    <div class="results-bar-right">
      <zaproo-select
        :model-value="filter.sort"
        name="sortBy"
        :label="$t('Sort by')"
        :removable="false"
        class="left-label-selector flex-1 md:flex-none"
        placeholder-classes="w-full md:w-[136px]"
        value-classes="w-full md:w-[136px]"
        options-wrapper-classes="left-label-selector-options"
        @update:model-value="
          emit('onFilterChange', {
            key: 'sort',
            val: $event,
          })
        "
      >
        <zaproo-select-option
          v-for="item in filteredSortOptions"
          :key="item.value"
          :value="item.value"
          class="left-label-selector-item"
          :class="{ selected: filter.sort === item.value }"
        >
          {{ item.label }}
        </zaproo-select-option>
      </zaproo-select>

      <zaproo-select
        v-if="perPageOptions?.length"
        :model-value="filter.size"
        name="perPage"
        :label="$t('Products per page')"
        :removable="false"
        placement="bottom-start"
        class="left-label-selector flex-1 md:flex-none"
        placeholder-classes="w-full overflow-hidden md:w-[46px]"
        value-classes="w-full md:w-[46px]"
        dropdown-classes="left-label-selector-dropdown !min-w-full"
        options-wrapper-classes="left-label-selector-options"
        @update:model-value="
          emit('onFilterChange', {
            key: 'size',
            val: $event,
          })
        "
      >
        <zaproo-select-option
          v-for="item in perPageOptions"
          :key="item.value"
          :value="item.value"
          class="left-label-selector-item"
          :class="{ selected: filter.size === item.value }"
        >
          {{ item.label }}
        </zaproo-select-option>
      </zaproo-select>
    </div>
    <div class="results-bar-bottom">
      <category-buttons-filter-button
        v-if="hasFiltersButton"
        :count="filtersQty"
        class="w-full justify-center"
        @click="$emit('openFilter')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PropType } from "vue";
import type { BaseSearchQueryParamsRawSchema } from "~/modules/catalog/types/BaseSearchQueryParamsSchema";
import type { SearchProductType } from "~/modules/settings/types";

const props = defineProps({
  productsQty: {
    type: [Number, String] as PropType<number | string>,
    default: 0,
  },
  filtersQty: {
    type: [Number, String] as PropType<number | string>,
    default: 0,
  },
  hasFiltersButton: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  filter: {
    type: Object as PropType<BaseSearchQueryParamsRawSchema>,
    required: true,
  },
  type: {
    type: [String, Number, undefined] as PropType<SearchProductType>,
    default: undefined,
  },
  loading: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
});

const emit = defineEmits<{
  (
    _e: "onFilterChange",
    _val: {
      key: keyof BaseSearchQueryParamsRawSchema;
      val: BaseSearchQueryParamsRawSchema[keyof BaseSearchQueryParamsRawSchema];
    },
  ): void;
  (_e: "openFilter"): void;
}>();

const { t } = useI18n();
const { perPageOptions, searchSortOptions } = useSettings();

const filteredSortOptions = computed(() =>
  searchSortOptions.filter(
    (item) =>
      props.type === undefined ||
      item.appliedOnly === undefined ||
      props.type === item.appliedOnly,
  ),
);
</script>

<style lang="scss">
.results-bar {
  @apply flex w-full flex-col gap-4 md:gap-6 lg:flex-row lg:items-center lg:justify-between;
  @apply border-b border-neutral-200 bg-white lg:bg-transparent;

  &-left {
    @apply gap-6 pt-4 md:pt-0;
  }

  &-count {
    @apply gap-1 py-2 text-lg font-semibold leading-6;
  }

  &-right {
    @apply flex items-center gap-2 md:justify-between md:gap-7 md:pb-6;

    .left-label-selector {
      @apply max-w-[50%] md:max-w-[unset];
    }
  }

  &-bottom {
    @apply py-4 md:hidden;
    @apply border-t border-neutral-200 bg-white lg:bg-transparent;
  }
}

.row {
  @apply flex items-center justify-between;
}
</style>
